// src/pages/CategoryPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

import ArticleList from "../components/ArticleList";
import CategoryList from "../components/CategoryList";
import TagList from "../components/TagList";
const API_BASE = process.env.REACT_APP_API_BASE || "http://192.168.1.89:8000/api";
const STORAGE_LANG_KEY = "site_lang";
const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

const norm = (d) => (Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : []);

const isCanceled = (e) =>
  e?.code === "ERR_CANCELED" ||
  e?.name === "CanceledError" ||
  e?.name === "AbortError" ||
  String(e?.message || "").toLowerCase().includes("cancel");

function getField(obj, field, lang) {
  if (!obj) return null;

  if (Array.isArray(obj.translations)) {
    const tr = obj.translations.find((t) =>
      String(t?.target_lang || t?.lang || "").startsWith(lang)
    );
    if (tr?.[field]) return tr[field];
  }

  if (obj.translations?.[lang]?.[field]) return obj.translations[lang][field];
  if (obj[`${field}_${lang}`]) return obj[`${field}_${lang}`];
  if (obj.i18n?.[lang]?.[field]) return obj.i18n[lang][field];

  return obj[field] ?? null;
}

const CategoryPage = () => {
  const { slug, lang: urlLang } = useParams();

  const [lang, setLang] = useState(() => {
    try {
      return urlLang || localStorage.getItem(STORAGE_LANG_KEY) || "en";
    } catch {
      return "en";
    }
  });

  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onSiteLang = (e) => {
      const nl = e?.detail?.lang;
      if (nl && nl !== lang) setLang(nl);
    };

    const onStorage = (e) => {
      if (e.key === STORAGE_LANG_KEY && e.newValue && e.newValue !== lang) {
        setLang(e.newValue);
      }
    };

    window.addEventListener("site:language_changed", onSiteLang);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("site:language_changed", onSiteLang);
      window.removeEventListener("storage", onStorage);
    };
  }, [lang]);

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);

      try {
        const cfg = {
          signal,
          params: { lang },
          headers: { "Accept-Language": lang },
        };

        const [catRes, artRes, allCatsRes, tagsRes] = await Promise.all([
          axios.get(`${API_BASE}/categories/${slug}/`, cfg),
          axios.get(`${API_BASE}/categories/${slug}/articles/`, cfg),
          axios.get(`${API_BASE}/categories/`, cfg),
          axios.get(`${API_BASE}/tags/`, cfg),
        ]);

        setCategory(catRes.data ?? null);
        setArticles(norm(artRes.data));
        setCategories(norm(allCatsRes.data));
        setTags(norm(tagsRes.data));
      } catch (e) {
        if (isCanceled(e)) return;
        setError(e);
        setCategory(null);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    },
    [slug, lang]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
    return () => ctrl.abort();
  }, [load]);

  const catName = getField(category, "name", lang) || category?.name || "";
  const catDesc = getField(category, "description", lang) || category?.description || "";

  const pageTitle = useMemo(
    () => `${catName || "Category"} | 24News`,
    [catName]
  );

  const pageDescription = useMemo(() => {
    const text =
      catDesc ||
      `Articles in ${catName || "this category"} on 24News.`;
    return String(text).replace(/\s+/g, " ").trim().slice(0, 160);
  }, [catDesc, catName]);

  const categoryImage = category?.image || category?.image_url || "";

  if (loading)
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );

  if (error && !category)
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          Failed to load category. {error?.message}
        </div>
      </div>
    );

  if (!category)
    return (
      <div className="container py-5">
        <h2>Category not found</h2>
      </div>
    );

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: lang || "en",
          dir: lang === "ar" ? "rtl" : "ltr",
        }}
      >
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {categoryImage ? <meta property="og:image" content={categoryImage} /> : null}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {categoryImage ? <meta name="twitter:image" content={categoryImage} /> : null}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: pageTitle,
            description: pageDescription,
            mainEntity: {
              "@type": "Thing",
              name: catName || "",
              description: catDesc || "",
            },
          })}
        </script>
        <link rel="alternate" hrefLang="en" href={`${baseUrl}/en/categories/${slug}`} />
         <link rel="alternate" hrefLang="ar" href={`${baseUrl}/ar/categories/${slug}`} />
         <link rel="alternate" hrefLang="de" href={`${baseUrl}/de/categories/${slug}`} />
         <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en/categories/${slug}`} />
         <link rel="canonical" href={`${window.location.origin}/${lang}/categories/${slug}`} />

      </Helmet>

      <div className="container">
        <div className="py-4">
          <h1 className="mb-2">{catName}</h1>
          {catDesc && <p className="text-muted mb-3">{catDesc}</p>}

          <div className="row g-4">
            <main className="col-lg-8">
              {articles.length ? (
                <ArticleList articles={articles} lang={lang} />
              ) : (
                <p className="text-muted">No articles in this category yet.</p>
              )}

            </main>

            <aside className="col-lg-4">
              <div className="sticky-top" style={{ top: 92 }}>
                <section className="card shadow-sm mb-4">
                  <div className="card-body">
                    <h5 className="card-title">All Categories</h5>
                    <CategoryList categories={categories} lang={lang} />
                  </div>
                </section>

                <section className="card shadow-sm mb-4">
                  <div className="card-body">
                    <h5 className="card-title">Tags</h5>
                    <TagList tags={tags} lang={lang} />
                  </div>
                </section>

              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
